import { useEffect, useRef, useState } from "react";
import { useFormContext, Controller } from "react-hook-form";
import CircularProgress from "./CircularProgress";
import { GetImages } from "../../utils/GetImages";
import "./FileUploader.css";
import { DeleteRedIcon } from "../../assets/svgs/DeleteRedIcon";
import SvgRoundIconButton from "../buttons/Svg-Round-Icon-button";
import {
  getFileExtension,
  JPEG_PNG_EXTENSION_ERROR,
} from "./imageUploadConstants";

interface MultipleFileUploaderProps {
  name: string;
  uploaderId: string;
  label: string;
  uploadertext?: string;
  required?: boolean;
  disabled?: boolean;
  requiredMessage?: string;
  allowedFormats?: string[];
  /** When set, file extension is validated in addition to MIME type (e.g. block .jfif). */
  allowedExtensions?: string[];
  maxSizeMB?: number;
  maxFiles?: number;
  resetTrigger?: number;
  uploadertypeslabel?: string;
  defaultImages?: Array<{ src: string; name: string }>;
  /** Skip the canvas PNG re-encode and keep the original file as-is. Default true preserves existing behavior. */
  convertImagesToPng?: boolean;
}

const MultipleFileUploader = ({
  name,
  uploaderId,
  label,
  required = false,
  disabled = false,
  requiredMessage = "This field is required",
  allowedFormats = ["image/jpeg", "image/png", "application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
  allowedExtensions,
  maxSizeMB = 1,
  maxFiles = 5,
  resetTrigger = 0,
  uploadertext = "Upload Files",
  uploadertypeslabel = `Max. size ${maxSizeMB}MB. ${allowedFormats.map((fmt) => fmt.split("/")[1]).join(", ")} (Max ${maxFiles} files)`,
  defaultImages = [],
  convertImagesToPng = true,
}: MultipleFileUploaderProps) => {
  const {
    control,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext();

  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const initializedRef = useRef(false);

  const [filesMap, setFilesMap] = useState<Record<string, { file: File; preview?: string }[]>>({});
  const files = filesMap[name] || [];
  const filesRef = useRef(files);
  filesRef.current = files;
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  /** Sticky reject message shown next to the field. A rejected selection is never
   *  committed, so this alone only blocks Submit when the field is required -- an
   *  optional field may still submit empty despite a lingering rejection message. */
  const [rejectionError, setRejectionError] = useState<string | null>(null);

  // Normalize base64 to data URL
  const normalizeBase64Url = (input: string) => {
    if (input.startsWith('data:')) return input;
    if (input.startsWith('iVBORw0KGgo=')) return `data:image/png;base64,${input}`;
    if (input.startsWith('/9j/')) return `data:image/jpeg;base64,${input}`;
    return `data:image/png;base64,${input}`; // Default PNG
  };

  // Convert any base64 to PNG data URL using canvas
  const convertToPNGDataURL = (base64Input: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png')); // Force PNG
        } else {
          reject(new Error('Canvas context not available'));
        }
      };
      img.onerror = reject;
      img.src = normalizeBase64Url(base64Input);
    });
  };

  // Convert base64 to PNG File object
  const base64ToPNGFile = async (base64Input: string, fileName: string = 'image.png'): Promise<File | null> => {
    try {
      const pngDataUrl = await convertToPNGDataURL(base64Input);
      const base64Data = pngDataUrl.split(',')[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/png' });
      return new File([blob], fileName, { type: 'image/png' });
    } catch (error) {
      console.error('Base64 to PNG conversion error:', error);
      return null;
    }
  };

  // Initialize with defaultImages
  // Initialize with defaultImages
useEffect(() => {
    if (!defaultImages || !defaultImages.length || initializedRef.current) return;
    initializedRef.current = true;

    const processDefaultImages = async () => {
        const processedFiles: { file: File; preview?: string }[] = [];
        for (let i = 0; i < defaultImages.length && i < maxFiles; i++) {
            const defaultImage = defaultImages[i];
            const normalized = normalizeBase64Url(defaultImage.src);

            const file = await base64ToPNGFile(defaultImage.src, defaultImage.name);
            if (file) {
                processedFiles.push({ file, preview: normalized });
            }
        }
        if (processedFiles.length > 0) {
            setFilesMap((prev) => ({ ...prev, [name]: processedFiles }));
            // Existing, already-saved attachments shouldn't be re-validated against the current
            // size cap just for being displayed on load -- only newly-selected files (handleFileChange)
            // should trigger the size check. Without this, a previously-fine attachment can start
            // failing a *tightened* cap (e.g. 5MB -> 1MB) purely from being shown, with no user action.
            setValue(name, processedFiles.map((item) => item.file), { shouldValidate: false });
        }
    };

    processDefaultImages();
}, [defaultImages, name, setValue, maxFiles]);


  // Reset on trigger
  useEffect(() => {
    if (resetTrigger === 0) return; // Skip initial render
    filesRef.current.forEach((item) => {
      if (item.preview?.startsWith("blob:")) URL.revokeObjectURL(item.preview);
    });
    setFilesMap((prev) => ({ ...prev, [name]: [] }));
    setValue(name, [], { shouldValidate: true });
    setRejectionError(null);
    clearErrors(name);
    initializedRef.current = false;
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [resetTrigger, setValue, clearErrors, name]);

  // Revoke any remaining object-URL previews on unmount to avoid leaking blob URLs.
  useEffect(() => {
    return () => {
      filesRef.current.forEach((item) => {
        if (item.preview?.startsWith("blob:")) URL.revokeObjectURL(item.preview);
      });
    };
  }, []);

  const clearProgress = () => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return prev;
        }
        return prev + 10;
      });
    }, 100);
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.includes("image")) return GetImages.imageIcon || GetImages.upload;
    if (fileType.includes("pdf")) return GetImages.pdfImage || GetImages.upload;
    if (fileType.includes("msword") || fileType.includes("wordprocessingml")) return GetImages.DocIcon || GetImages.upload;
    if (fileType.includes("ms-excel") || fileType.includes("spreadsheetml")) return GetImages.ExcelIcon || GetImages.upload;
    return GetImages.upload;
  };

  const acceptAttribute = allowedExtensions?.length
    ? [...allowedExtensions, ...allowedFormats].join(",")
    : allowedFormats.join(",");

  const validateFile = (file: File | string) => {
    if (file instanceof File) {
      if (!allowedFormats.includes(file.type)) {
        return `Only ${allowedFormats.map((fmt) => fmt.split("/")[1]).join(", ")} allowed`;
      }
      if (allowedExtensions?.length) {
        const extension = getFileExtension(file.name);
        if (!allowedExtensions.includes(extension)) {
          return JPEG_PNG_EXTENSION_ERROR;
        }
      }
      const sizeInMB = file.size / (1024 * 1024);
      if (sizeInMB > maxSizeMB) {
        return `Max file size is ${maxSizeMB}MB`;
      }
      return true;
    }

    if (typeof file === 'string') {
      const normalized = normalizeBase64Url(file);
      const mimeMatch = normalized.match(/data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+);base64,/);
      const mimeType = mimeMatch ? mimeMatch[1] : "image/png";

      if (!allowedFormats.includes(mimeType)) {
        return `Only ${allowedFormats.map((fmt) => fmt.split("/")[1]).join(", ")} allowed`;
      }

      let base64Data = normalized.split(',')[1] || normalized;
      const sizeInBytes = Math.floor((base64Data.length * 3) / 4);
      const sizeInMB = sizeInBytes / (1024 * 1024);
      if (sizeInMB > maxSizeMB) {
        return `Max file size is ${maxSizeMB}MB`;
      }
      return true;
    }

    return `Invalid file format`;
  };

  const handleFileChange = async (newFiles: FileList | null, fieldOnChange: (value: File[] | null) => void) => {
    if (!newFiles || newFiles.length === 0) {
      console.log('No files selected in handleFileChange');
      return;
    }

    clearErrors(name);

    if (files.length + newFiles.length > maxFiles) {
      const message = `You can upload maximum ${maxFiles} files`;
      setRejectionError(message);
      setError(name, {
        type: "manual",
        message,
      });
      return;
    }

    const validFiles: { file: File; preview?: string }[] = [];
    let validationError = "";

    for (let i = 0; i < newFiles.length; i++) {
      const file = newFiles[i];
      const result = validateFile(file);
      if (result !== true) {
        validationError = result;
        break;
      }

      if (file.type.includes("image") && convertImagesToPng) {
        const reader = new FileReader();
        const pngFilePromise = new Promise<{ file: File; preview: string }>((resolve, reject) => {
          reader.onload = async (e) => {
            const base64 = e.target?.result as string;
            try {
              const pngDataUrl = await convertToPNGDataURL(base64);
              const pngFile = await base64ToPNGFile(base64, file.name.replace(/\.[^/.]+$/, ".png"));
              if (pngFile) {
                resolve({ file: pngFile, preview: pngDataUrl });
              } else {
                reject(new Error("Failed to convert image to PNG"));
              }
            } catch (error) {
              reject(error);
            }
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        try {
          const result = await pngFilePromise;
          validFiles.push(result);
        } catch (error) {
          validationError = "Image conversion failed";
          break;
        }
      } else if (file.type.includes("image")) {
        // Keep the original file bytes (no lossy PNG re-encode, so size stays accurate),
        // but still show a real thumbnail via a cheap object URL instead of a generic file icon.
        validFiles.push({ file, preview: URL.createObjectURL(file) });
      } else {
        validFiles.push({ file });
      }
    }

    if (validationError) {
      // Release any object-URL previews created for files earlier in this batch
      // before the rejection -- they never make it into filesMap, so none of the
      // other revoke paths (delete/reset/unmount) would ever see them.
      validFiles.forEach((item) => {
        if (item.preview?.startsWith("blob:")) URL.revokeObjectURL(item.preview);
      });
      setRejectionError(validationError);
      setError(name, {
        type: "manual",
        message: validationError,
      });
      return;
    }

    if (validFiles.length === 0) {
      console.log('No valid files after processing');
      return;
    }

    setRejectionError(null);
    setLoading(true);
    clearProgress();

    setTimeout(() => {
      setLoading(false);
      const updatedFiles = [...files, ...validFiles];
      setFilesMap((prev) => ({ ...prev, [name]: updatedFiles }));
      const fileArray = updatedFiles.map((item) => item.file);
      setValue(name, fileArray, { shouldValidate: true });
      fieldOnChange(fileArray);
    }, 1500);
  };

  const handleDelete = (index: number, onChange: (value: File[] | null) => void) => {
    const removedPreview = files[index]?.preview;
    if (removedPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(removedPreview);
    }
    const updatedFiles = files.filter((_, i) => i !== index);
    setFilesMap((prev) => ({ ...prev, [name]: updatedFiles }));
    const fileArray = updatedFiles.length > 0 ? updatedFiles.map((item) => item.file) : [];
    setValue(name, fileArray, { shouldValidate: true });
    onChange(fileArray);
    setRejectionError(null);
    clearErrors(name);
     if (fileInputRef.current) fileInputRef.current && (fileInputRef.current.value = "");
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        // Per-file format/extension/size validation happens once, at selection time
        // (handleFileChange -> validateFile, before a file is ever committed here) --
        // re-running it against the committed value on every submit would also re-check
        // already-saved attachments loaded from defaultImages, which must never be
        // rejected for a size/format cap that postdates when they were originally saved.
        //
        // A rejected selection is never committed to filesMap (handleFileChange returns
        // before touching it), so the only thing left to validate here is whatever was
        // already committed -- there's nothing left for a rejectionError check to add:
        // an optional field is fine empty, and a required field with no committed files
        // is already caught by the empty-value check below (whether that's because
        // nothing was ever selected, or the only selection so far was rejected).
        validate: (files: File[] | null) => {
          if (!required) return true;
          if (!files || files.length === 0) return requiredMessage;
          return true;
        },
      }}
      render={({ field: { onChange } }) => (
        <div className="form-group">
          <label className="fw-600 black">
            {label}{" "}
            {required ? (
              <span className="text-danger">*</span>
            ) : (
              <span className="optional-text-color">(Optional)</span>
            )}
          </label>
          <div className="uploaderContainer">
            <div className={`multiple-uploader-box-container border-image-dashed ${loading ? "uploding-bg-color" : ""}`}>
              <input
                disabled={disabled}
                type="file"
                ref={fileInputRef}
                id={`upload_${uploaderId}`}
                className="upload-input"
                onChange={(e) => {
                  handleFileChange(e.target.files, onChange);
                }}
                accept={acceptAttribute}
                multiple
              />
              <label htmlFor={`upload_${uploaderId}`} className="upload-label">
                <div className="emptyImage gap-3">
                  {loading ? (
                    <div className="d-flex flex-column align-items-center gap-2">
                      <CircularProgress
                        size={40}
                        thickness={3}
                        isIcon={false}
                        color="blue"
                        BG_transparent={true}
                        progress={progress}
                      />
                    </div>
                  ) : (
                    <img className="image-icon-size-30 iconStyleWithWhiteFilter" src={GetImages.upload} alt="add" />
                  )}
                  <div className="sub_text fw-700 black font-size-16">{uploadertext}</div>
                  <p className="sub_text font-size-15 grey_subtext1 mt-1">{uploadertypeslabel}</p>
                </div>
              </label>
            </div>

            <div className="row mt-2">
              {files.map((item, index) => (
                <div key={index} className="col-md-4 mb-2">
                  <div className="preview-container position-relative">
                    {item.file.type.includes("image") && item.preview ? (
                      <img
                        src={item.preview}
                        alt={`preview-${index}`}
                        className="preview-image"
                      />
                    ) : (
                      <div className="file-preview d-flex align-items-center justify-content-center">
                        <img
                          src={getFileIcon(item.file.type)}
                          alt={`icon-${index}`}
                          className="file-icon-size-50"
                        />
                        <span className="file-name-font-size">{item.file.name}</span>
                      </div>
                    )}
                    <SvgRoundIconButton
                      style={{ position: "absolute", top: -10, right: -10 }}
                      onClick={() => handleDelete(index, onChange)}
                      Icon={<DeleteRedIcon fill="#fff" width="22" height="22" />}
                    />
                  </div>
                </div>
              ))}
            </div>

            {(errors[name] || rejectionError) && (
              <div className="invalid-feedback d-block">
                {rejectionError ?? (errors[name]?.message as string)}
              </div>
            )}
          </div>
        </div>
      )}
    />
  );
};

export default MultipleFileUploader;